import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Define TypeScript interfaces for our data
interface Course {
  id: number,
  title: string;
  subject: "Physics" | "Chemistry" | "Biology" | "Mathematics";
  instructor: string;
  rating: number;
  reviewCount: number;
  price: number;
  discountPrice: number;
  imageUrl: string;
  board: string;
  mode: string;
  badges?: string[];
  topics: string[];
  description: string;
  defaultLanguage?: string;
  languageContent?: {
    [key: string]: {
      id?:number;
      title?: string;
      topics?: string[];
      description?: string;
    }
  }
}

interface ScienceCourseCardProps {
  course: Course;
}

const ScienceCourseCard: React.FC<ScienceCourseCardProps> = ({ course }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(course.defaultLanguage || 'English');
  
  // Calculate discount percentage
  const discountPercentage = Math.round(
    ((course.price - course.discountPrice) / course.price) * 100
  );
  
  // Subject-specific gradients
  const subjectGradients: Record<Course['subject'], string> = {
    "Physics": "from-blue-500 to-indigo-600",
    "Chemistry": "from-green-500 to-emerald-600",
    "Biology": "from-teal-500 to-green-600",
    "Mathematics": "from-purple-500 to-indigo-600"
  };
  
  const gradient = subjectGradients[course.subject] || "from-blue-500 to-indigo-600";
  
  // Render star rating
  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= Math.round(rating) ? "text-yellow-400" : "text-gray-300"}>
            ★
          </span>
        ))}
      </div>
    );
  };
  
  // Get content based on selected language
  // const getContent = (key: keyof Course) => {
  //   return course.languageContent?.[selectedLanguage]?.[key as keyof Course] ?? course[key];
  // };

  // Get content based on selected language
  // Get content based on selected language
  const getContent = (key: keyof Pick<Course, 'title' | 'topics' | 'description'>) => {
    if (course.languageContent && selectedLanguage in course.languageContent) {
      const langContent = course.languageContent[selectedLanguage];
      if (langContent && key in langContent && langContent[key] !== undefined) {
        return langContent[key];
      }
    }
    return course[key];
  };

  // Handle language change with explicit pointer events
  const changeLanguage = (language: string) => {
    setSelectedLanguage(language);
  };
  
  return (
    <div 
      className="w-72 bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl"
      style={{ transform: isHovered ? 'translateY(-8px)' : 'none' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Header with Image */}
      <div className={`relative h-40 bg-gradient-to-r ${gradient}`}>
        <img 
          src={course.imageUrl} 
          alt={getContent('title') as string}
          className="w-full h-full object-cover opacity-100"   //mix-blend-overlay
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {course.badges && course.badges.map((badge: string, index: number) => (
            <span 
              key={index} 
              className="px-2 py-1 bg-white/90 text-xs font-bold rounded-full shadow-sm text-purple-700"
            >
              {badge}
            </span>
          ))}
        </div>
        
        {/* Language Selector - Made smaller and more clickable */}
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-white rounded-md shadow-md flex overflow-hidden">
            <button 
              className={`px-1.5 py-0.5 text-xs font-medium cursor-pointer ${selectedLanguage === 'English' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => changeLanguage('English')}
              aria-label="Switch to English"
            >
              EN
            </button>
            <button 
              className={`px-1.5 py-0.5 text-xs font-medium cursor-pointer ${selectedLanguage === 'Hindi' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => changeLanguage('Hindi')}
              aria-label="Switch to Hindi"
            >
              हि
            </button>
          </div>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-800 line-clamp-2 h-14">{getContent('title')}</h3>
        
        <p className="text-sm text-gray-600 mt-2">by {course.instructor}</p>
        
        <div className="flex items-center mt-2">
          <span className="text-amber-500 font-bold mr-1">{course.rating}</span>
          {renderStars(course.rating)}
          <span className="text-xs text-gray-500 ml-1">({course.reviewCount})</span>
        </div>
        
        <div className="flex items-center mt-3 text-sm text-gray-600">
          <span className="flex items-center mr-4">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {course.mode}
          </span>
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {course.board}
          </span>
        </div>
        
        {/* Topics tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {getContent('topics') && (getContent('topics') as string[]).map((topic: string, index: number) => (
            <span 
              key={index} 
              className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
            >
              {topic}
            </span>
          ))}
        </div>
        
        {/* Medium tag */}
        <div className="mt-2">
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
            {selectedLanguage} Medium
          </span>
        </div>
        
        {/* Price and CTA */}
        <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between items-center">
          <div>
            <span className="text-lg font-bold text-gray-800">₹{course.discountPrice}</span>
            <span className="text-sm text-gray-500 line-through ml-2">₹{course.price}</span>
            <span className="ml-2 text-green-600 text-xs font-bold">{discountPercentage}% OFF</span>
          </div>
          
          <button 
            className={`px-3 py-2 rounded-lg font-medium text-sm text-white bg-gradient-to-r ${gradient} transition-all hover:shadow-md cursor-pointer`}
          >
            {selectedLanguage === 'Hindi' ? 'अभी जुड़ें' : 'Enroll Now'}
          </button>
        </div>
      </div>
      
      {/* Quick Preview Overlay (appears on hover) */}
      <div 
        className={`absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-5 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <h3 className="text-lg font-bold text-white">{getContent('title')}</h3>
        <p className="text-white/80 text-sm my-2">
          {getContent('description')}
        </p>
        
        <div className="flex gap-2 mt-3">
          <Link to={`/coursepage/${course.id}`} className="flex-1 bg-white text-purple-700 py-2 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors cursor-pointer">
            {selectedLanguage === 'Hindi' ? 'विवरण देखें' : 'View Details'}
          </Link>
          <Link to={`/coursepage/${course.id}`} className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-2 rounded-lg font-medium text-sm hover:from-purple-600 hover:to-indigo-700 transition-colors cursor-pointer">
            {selectedLanguage === 'Hindi' ? 'अभी जुड़ें' : 'Enroll Now'}
          </Link>
        </div>
      </div>
    </div>
  );
};

const ScienceCoursesApp = () => {
  const [activeClass, setActiveClass] = useState('class12');

  // Add these state variables for scroll indicators
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  // Add this useEffect to handle scroll indicators
  useEffect(() => {
    const container = document.getElementById('courseScrollContainer');
    
    if (container) {
      const handleScroll = () => {
        const isNotAtStart = container.scrollLeft > 10;
        const isNotAtEnd = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
        
        setShowLeftArrow(isNotAtStart);
        setShowRightArrow(isNotAtEnd);
      };
      
      // Initial check
      handleScroll();
      
      // Add scroll event listener
      container.addEventListener('scroll', handleScroll);
      
      // Cleanup
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [activeClass]); // Re-run when active class changes
  
  // Add these scroll handler functions
  const scrollLeft = () => {
    const container = document.getElementById('courseScrollContainer');
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    const container = document.getElementById('courseScrollContainer');
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  
  // Class 12 Courses Data
  const class12Courses: Course[] = [
    {
      id: 3,
      title: "Class 12 Physics - Complete Course",
      subject: "Physics",
      instructor: "Danish Sir",
      rating: 4.9,
      reviewCount: 1256,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/8_20241024_203804_0007.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["Bestseller", "Top Rated"],
      topics: ["Theory", "Problem Solving", "Numericals"],
      description: "Master Class 12 Physics with comprehensive theory lessons and problem-solving techniques. Includes PDF notes, class notes, and practice tests.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          title: "कक्षा 12 भौतिक विज्ञान - संपूर्ण कोर्स",
          topics: ["सिद्धांत", "प्रश्न हल", "संख्यात्मक"],
          description: "व्यापक सिद्धांत पाठों और समस्या-समाधान तकनीकों के साथ कक्षा 12 भौतिक विज्ञान में महारत हासिल करें। पीडीएफ नोट्स, क्लास नोट्स और अभ्यास परीक्षण शामिल हैं।"
        }
      }
    },
    {
      id: 1,
      title: "Class 12 Chemistry - Complete Course",
      subject: "Chemistry",
      instructor: "MG Hussain",
      rating: 4.8,
      reviewCount: 1078,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/10_20241024_203804_0009.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["Most Popular", "High Success Rate"],
      topics: ["Organic", "Inorganic", "Physical"],
      description: "Comprehensive Chemistry course covering organic, inorganic and physical chemistry with practical applications and exam strategies.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 1,
          title: "कक्षा 12 रसायन विज्ञान - संपूर्ण कोर्स",
          topics: ["कार्बनिक", "अकार्बनिक", "भौतिक"],
          description: "व्यापक रसायन विज्ञान पाठ्यक्रम जो व्यावहारिक अनुप्रयोगों और परीक्षा रणनीतियों के साथ कार्बनिक, अकार्बनिक और भौतिक रसायन विज्ञान को कवर करता है।"
        }
      }
    },
    {
      id: 5,
      title: "Class 12 Biology - Complete Course",
      subject: "Biology",
      instructor: "Danish Sir",
      rating: 4.9,
      reviewCount: 982,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/12_20241024_203804_0011.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["High Scoring", "NEET Focused"],
      topics: ["Botany", "Zoology", "Human Physiology"],
      description: "In-depth Biology course with detailed diagrams, case studies and NEET-focused content. Includes PDF notes and regular tests.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 5,
          title: "कक्षा 12 जीव विज्ञान - संपूर्ण कोर्स",
          topics: ["वनस्पति विज्ञान", "प्राणी विज्ञान", "मानव शरीर विज्ञान"],
          description: "विस्तृत आरेखों, केस स्टडीज और NEET-केंद्रित सामग्री के साथ गहन जीव विज्ञान पाठ्यक्रम। पीडीएफ नोट्स और नियमित परीक्षण शामिल हैं।"
        }
      }
    },
    {
      id: 33,
      title: "Class 12 Mathematics - Complete Course",
      subject: "Mathematics",
      instructor: "MG Hussain Sir",
      rating: 4.7,
      reviewCount: 1345,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/maths_12_board_ghJKkOH.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["JEE Advanced", "Conceptual"],
      topics: ["Calculus", "Algebra", "Vectors"],
      description: "Master Class 12 Math with simplified approach to complex topics. Strong focus on JEE preparation with specialized problem-solving techniques.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {

          id: 33,
          title: "कक्षा 12 गणित - संपूर्ण कोर्स",
          topics: ["कैलकुलस", "बीजगणित", "सदिश"],
          description: "जटिल विषयों के लिए सरलीकृत दृष्टिकोण के साथ कक्षा 12 गणित में महारत हासिल करें। विशेष समस्या-समाधान तकनीकों के साथ JEE तैयारी पर मजबूत फोकस।"
        }
      }
    }
  ];
  
  // Class 11 Courses Data
  const class11Courses: Course[] = [
    {
      id: 2,
      title: "Class 11 Physics - Complete Course",
      subject: "Physics",
      instructor: "Danish Sir",
      rating: 4.9,
      reviewCount: 1345,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/1_20241024_203803_0000.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["Bestseller", "Foundation"],
      topics: ["Mechanics", "Thermodynamics", "Waves"],
      description: "Build a strong Physics foundation with focus on fundamentals and problem-solving techniques. Includes animated visualizations, practice worksheets and regular tests.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 2,
          title: "कक्षा 11 भौतिक विज्ञान - संपूर्ण कोर्स",
          topics: ["यांत्रिकी", "ऊष्मागतिकी", "तरंगें"],
          description: "मूल सिद्धांतों और समस्या-समाधान तकनीकों पर ध्यान देने के साथ मजबूत भौतिकी की नींव बनाएं। एनिमेटेड विज़ुअलाइज़ेशन, अभ्यास वर्कशीट और नियमित परीक्षण शामिल हैं।"
        }
      }
    },
    {
      id: 6,
      title: "Class 11 Chemistry - Complete Course",
      subject: "Chemistry",
      instructor: "Ahmad Sir",
      rating: 4.8,
      reviewCount: 1267,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/9_20241024_203804_0008.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["Top Rated", "JEE Focused"],
      topics: ["Physical", "Organic", "Inorganic"],
      description: "Comprehensive Chemistry course with strong focus on fundamentals, conceptual clarity and visualization techniques. Includes lab demos, interactive sessions and practice tests.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 6,
          title: "कक्षा 11 रसायन विज्ञान - संपूर्ण कोर्स",
          topics: ["भौतिक", "कार्बनिक", "अकार्बनिक"],
          description: "मूल सिद्धांतों, अवधारणात्मक स्पष्टता और विजुअलाइजेशन तकनीकों पर मजबूत फोकस के साथ व्यापक रसायन विज्ञान पाठ्यक्रम। लैब डेमो, इंटरैक्टिव सेशन और प्रैक्टिस टेस्ट शामिल हैं।"
        }
      }
    },
    {
      id: 4,
      title: "Class 11 Biology - Complete Course",
      subject: "Biology",
      instructor: "Danish Sir",
      rating: 4.9,
      reviewCount: 1098,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/11_20241024_203804_0010.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["NEET Foundation", "High Success"],
      topics: ["Cell Biology", "Plant Physiology", "Human Physiology"],
      description: "In-depth Biology course with detailed diagrams, microscopic observations and NEET-focused content. Builds strong foundation for Class 12 and medical entrance exams.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 4,
          title: "कक्षा 11 जीव विज्ञान - संपूर्ण कोर्स",
          topics: ["कोशिका विज्ञान", "पादप शरीर क्रिया विज्ञान", "मानव शरीर क्रिया विज्ञान"],
          description: "विस्तृत आरेखों, सूक्ष्मदर्शी अवलोकनों और NEET-केंद्रित सामग्री के साथ गहन जीव विज्ञान पाठ्यक्रम। कक्षा 12 और मेडिकल प्रवेश परीक्षाओं के लिए मजबूत आधार बनाता है।"
        }
      }
    },
    {
      id: 34,
      title: "Class 11 Mathematics - Complete Course",
      subject: "Mathematics",
      instructor: "MG Hussain",
      rating: 4.7,
      reviewCount: 1456,
      price: 4999,
      discountPrice: 999,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/maths_11_board_1_KpohhGq.png",
      board: "CBSE & Bihar",
      mode: "Online + Offline",
      badges: ["JEE Foundation", "Conceptual"],
      topics: ["Algebra", "Trigonometry", "Coordinate Geometry"],
      description: "Build a strong mathematical foundation with conceptual clarity and problem-solving techniques. Includes multiple practice sessions, doubt clearing and JEE preparation material.",
      defaultLanguage: "English",
      languageContent: {
        "Hindi": {
          id: 34,
          title: "कक्षा 11 गणित - संपूर्ण कोर्स",
          topics: ["बीजगणित", "त्रिकोणमिति", "निर्देशांक ज्यामिति"],
          description: "अवधारणात्मक स्पष्टता और समस्या-समाधान तकनीकों के साथ मजबूत गणितीय आधार बनाएं। कई अभ्यास सत्र, संदेह निवारण और JEE तैयारी सामग्री शामिल है।"
        }
      }
    }
  ];

  return (
    <div className="bg-gray-100 p-6">
      {/* Header and Toggle Buttons */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">Science Courses</h1>
        
        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={() => setActiveClass('class11')}
            className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
              activeClass === 'class11'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Class 11 Science
          </button>
          
          <button
            onClick={() => setActiveClass('class12')}
            className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
              activeClass === 'class12'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Class 12 Science
          </button>
        </div>
        
        {/* Active class description */}
        <div className="text-center text-gray-600 mb-6">
          {activeClass === 'class11' ? (
            <p>Foundation courses for Class 11 students. Focus on building strong fundamentals.</p>
          ) : (
            <p>Complete preparation for Class 12 board exams and competitive entrance tests.</p>
          )}
        </div>
      </div>
      
      {/* Course Cards */}
      <div className="relative max-w-6xl mx-auto">
        {/* Left scroll indicator */}
        <div 
          className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 p-3 rounded-full shadow-lg cursor-pointer transition-all duration-300 hover:bg-purple-100 ${showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={scrollLeft}
        >
          <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </div>

        {/* Right scroll indicator */}
        <div 
          className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 p-3 rounded-full shadow-lg cursor-pointer transition-all duration-300 hover:bg-purple-100 ${showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={scrollRight}
        >
          <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        
        {/* Scroll container */}
        <div className="flex overflow-x-auto gap-6 pb-4 scroll-container hide-scrollbar" id="courseScrollContainer">
          <div className="flex flex-nowrap gap-6">
            {(activeClass === 'class11' ? class11Courses : class12Courses).map((course, index) => (
              <ScienceCourseCard key={index} course={course} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScienceCoursesApp;